import React, { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { ProfileSection, BillingSection } from "./CustomerProfileSections";
import BillingOverridePanel from "./BillingOverridePanel";

// Mounted by the current CustomerDetail route; all mutations continue to
// use the existing MFA-protected, audited platform-admin endpoints.
export default function CustomerAccountPanel({ tenantId, section }) {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const generation = useRef(0);
  const load = useCallback(async () => {
    const request = ++generation.current;
    setLoading(true);
    setError(null);
    try {
      if (section === "profile") {
        const response = await api.get(`/platform/tenants/${tenantId}/profile`);
        if (request === generation.current) setData({ profile: response.data });
      } else {
        const [overview, catalogue] = await Promise.all([
          api.get(`/platform/tenants/${tenantId}/overview`),
          api.get("/trials/plans"),
        ]);
        if (request === generation.current) {
          setData({ ...overview.data, plans: catalogue.data.plans || [] });
        }
      }
    } catch (e) {
      if (request === generation.current) {
        const detail = e?.response?.data?.detail;
        setError(typeof detail === "string" ? detail : "Unable to load company settings.");
      }
    } finally {
      if (request === generation.current) setLoading(false);
    }
  }, [tenantId, section]);
  useEffect(() => { load(); return () => { generation.current += 1; }; }, [load]);

  if (loading) return <p role="status" className="p-4">Loading company settings…</p>;
  if (error) return (
    <div role="alert" className="p-4 space-y-3">
      <p>{error}</p>
      {/MFA|two-factor/i.test(error) && <Link to="/settings">Open authentication settings</Link>}
      <button className="btn btn-primary" onClick={load}>Retry</button>
    </div>
  );
  if (!data) return null;
  if (section === "profile") {
    return <ProfileSection profile={data.profile} tenantId={tenantId} onReload={load} />;
  }
  return (
    <div className="space-y-5">
      <BillingOverridePanel tenant={data.tenant} />
      <BillingSection tenant={data.tenant} plan={data.plan}
        entitlements={data.entitlements} planHistory={data.plan_history}
        plans={data.plans} tenantId={tenantId} onReload={load} />
    </div>
  );
}
